import { Spinner, useOutsideClick } from "@swivl-mono/ui";
import { memo, useEffect } from "react";

import { API } from "@swivl-mono/api-helper";
import { filterClasses, VarsHelper } from "@swivl-mono/helpers";
import { UserInterface, UserVarKey } from "@swivl-mono/types";
import { useParams } from "react-router-dom";
import useAutoFocus from "../../../Hooks/useAutoFocus";
import useKeyPressAction from "../../../Hooks/useKeyPressAction";
import { Member } from "../../../Models/Member/Member.Current";
import { QuickReplyItem, QuickReplyModel } from "../../../Models/QuickReply/QuickReply.Model";
import { UsersModel } from "../../../Models/Users/Users.Model";

interface QuickRepliesDropdownProps {
  isLoading: boolean;
  isQuickRepliesDropdownOpen?: boolean;
  handleSetText: (text: string) => void;
  handleToggleQuickRepliesDropdown: (isOpen: boolean) => void;
  quickReplies?: QuickReplyItem[];
  user: UserInterface;
}

const BaseQuickRepliesDropdown = ({
  isLoading,
  quickReplies,
  isQuickRepliesDropdownOpen,
  handleToggleQuickRepliesDropdown,
  handleSetText,
  user
}: QuickRepliesDropdownProps) => {
  const menuRef = useOutsideClick(() => handleToggleQuickRepliesDropdown(false));
  const { greeting } = QuickReplyModel.useState((s) => s);
  const params = useParams();
  const { userId } = params;
  const { users } = UsersModel.useState((s) => s);
  const currentUser = userId && users?.[userId];
  useAutoFocus(menuRef, [isQuickRepliesDropdownOpen]);

  useKeyPressAction({
    callback: () => handleToggleQuickRepliesDropdown(false),
    key: "Escape",
    targetEl: menuRef.current,
  });

  const handleSetQuickReply = async (QuickReply: QuickReplyItem) => {
    if (currentUser && user) {
      await API.UserVars.saveUserVar(user.orgId, user.id, UserVarKey.LastAgentName, Member.displayName);
      const message = await VarsHelper.replaceVariablesInString(currentUser, QuickReply.message);
      handleSetText(message);
      handleToggleQuickRepliesDropdown(false);
    } else {
      console.warn("User not found");
    }
  };

  useEffect(() => {
    if (greeting) {
      handleSetQuickReply(greeting);
    }
  }, [greeting]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div
      aria-expanded={isQuickRepliesDropdownOpen}
      className={
        filterClasses(
          "overflow-y-auto min-h-0 transition-all duration-300 ease-in-out w-full shadow-lg rounded-b-md [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-grey-400 [&::-webkit-scrollbar-thumb]:rounded-md",
          isQuickRepliesDropdownOpen ? "py-1 max-h-32 visible mt-3.5" : "py-0 mt-0 max-h-0 invisible"
        )}
      ref={menuRef}
    >
      <div
        className={filterClasses(
          "flex flex-col transition-all duration-1000 gap-2"
        )}
      >
        {quickReplies?.map((quickReply) => (
          <div className="px-2 w-full flex flex-col hover:bg-grey-300 transition-colors duration-200" key={quickReply.id}>
            <button className="text-left" onClick={() => handleSetQuickReply(quickReply)}>
              <span className="py-2 text-grey-600">{quickReply.name}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const QuickRepliesDropdown = memo(BaseQuickRepliesDropdown);
